'use client';

/**
 * MatchScore Component
 *
 * Displays a match score with color-coded level indicator.
 * Part of the identity resolution component suite.
 */

import Box from '@mui/material/Box';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import { forwardRef, useMemo } from 'react';
import { baseColors, getNeonStyles } from '../../theme/palette';
import { transitionPresets } from '../../theme/transitions';
import type { MatchLevel, MatchScoreProps } from '../../types/identityResolution';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Constants
// ============================================================================

const SCORE_COLORS: Record<MatchLevel, { base: string; bg: string; border: string }> = {
  EXACT: {
    base: baseColors.success[500],
    bg: alpha(baseColors.success[500], 0.12),
    border: alpha(baseColors.success[500], 0.3),
  },
  PROBABLE: {
    base: baseColors.warning[500],
    bg: alpha(baseColors.warning[500], 0.12),
    border: alpha(baseColors.warning[500], 0.3),
  },
  POSSIBLE: {
    // Tailwind orange-500 — distinct from warning amber so POSSIBLE reads
    // as "weaker than PROBABLE" rather than mirroring the amber palette.
    base: '#F97316',
    bg: alpha('#F97316', 0.12),
    border: alpha('#F97316', 0.3),
  },
  NO_MATCH: {
    base: baseColors.neutral[500],
    bg: alpha(baseColors.neutral[500], 0.12),
    border: alpha(baseColors.neutral[500], 0.3),
  },
};

const LEVEL_LABELS: Record<MatchLevel, string> = {
  EXACT: 'Exact Match',
  PROBABLE: 'Probable Match',
  POSSIBLE: 'Possible Match',
  NO_MATCH: 'No Match',
};

const SIZE_CONFIGS = {
  small: { container: 48, ring: 44, text: '0.75rem', label: '0.625rem' },
  medium: { container: 64, ring: 56, text: '1rem', label: '0.75rem' },
  large: { container: 80, ring: 72, text: '1.25rem', label: '0.875rem' },
};

// ============================================================================
// Styled Components
// ============================================================================

interface StyledContainerProps {
  $size: 'small' | 'medium' | 'large';
  $showLabel: boolean;
}

const StyledContainer = styled(
  Box,
  transientOptions,
)<StyledContainerProps>(({ $showLabel }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: $showLabel ? 8 : 0,
}));

interface StyledScoreRingProps {
  $size: 'small' | 'medium' | 'large';
  $color: string;
  $bgColor: string;
  $borderColor: string;
  $isDark: boolean;
}

const StyledScoreRing = styled(
  motion.div,
  transientOptions,
)<StyledScoreRingProps>(({ $size, $color, $bgColor, $borderColor, $isDark }) => {
  const config = SIZE_CONFIGS[$size];

  return {
    position: 'relative',
    width: config.container,
    height: config.container,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: $bgColor,
    border: `2px solid ${$borderColor}`,
    transition: transitionPresets.all,

    // Inner score text
    '& .score-text': {
      fontSize: config.text,
      fontWeight: 700,
      color: $color,
      fontVariantNumeric: 'tabular-nums',
    },

    // Progress ring (SVG)
    '& .progress-ring': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      transform: 'rotate(-90deg)',

      '& circle': {
        fill: 'none',
        strokeWidth: 3,
        strokeLinecap: 'round',
      },

      '& .ring-bg': {
        stroke: $isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
      },

      '& .ring-progress': {
        stroke: $color,
        transition: 'stroke-dashoffset 0.6s cubic-bezier(0.215, 0.61, 0.355, 1)',
      },
    },
  };
});

interface StyledLabelProps {
  $color: string;
  $size: 'small' | 'medium' | 'large';
}

const StyledLabel = styled(
  Typography,
  transientOptions,
)<StyledLabelProps>(({ $color, $size }) => ({
  fontSize: SIZE_CONFIGS[$size].label,
  fontWeight: 600,
  color: $color,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}));

// ============================================================================
// Component
// ============================================================================

export const MatchScore = forwardRef<HTMLDivElement, MatchScoreProps>(
  ({ score, level, size = 'medium', showLabel = true, neonAccent }, ref) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Get colors based on level
    const colors = useMemo(() => {
      if (neonAccent) {
        const neonStyles = getNeonStyles(neonAccent, isDark);
        return {
          base: neonStyles.base,
          bg: neonStyles.background,
          border: neonStyles.border,
        };
      }
      return SCORE_COLORS[level];
    }, [level, neonAccent, isDark]);

    // Calculate ring progress
    const config = SIZE_CONFIGS[size];
    const ringRadius = (config.ring - 6) / 2; // Account for stroke width
    const circumference = 2 * Math.PI * ringRadius;
    const progress = (score / 100) * circumference;
    const dashOffset = circumference - progress;

    return (
      <StyledContainer ref={ref} $size={size} $showLabel={showLabel}>
        <StyledScoreRing
          $size={size}
          $color={colors.base}
          $bgColor={colors.bg}
          $borderColor={colors.border}
          $isDark={isDark}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3, ease: [0.215, 0.61, 0.355, 1] }}
        >
          <svg className="progress-ring" viewBox={`0 0 ${config.container} ${config.container}`}>
            <circle className="ring-bg" cx={config.container / 2} cy={config.container / 2} r={ringRadius} />
            <circle
              className="ring-progress"
              cx={config.container / 2}
              cy={config.container / 2}
              r={ringRadius}
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
            />
          </svg>
          <span className="score-text">{Math.round(score)}</span>
        </StyledScoreRing>

        {showLabel && (
          <StyledLabel $color={colors.base} $size={size}>
            {LEVEL_LABELS[level]}
          </StyledLabel>
        )}
      </StyledContainer>
    );
  },
);

MatchScore.displayName = 'MatchScore';

export default MatchScore;
